import apiClient from './client';

/** Summary row for the Payroll Review client list. */
export interface PayrollClientSummary {
  clientId: string;
  displayName: string;
  industry: string | null;
  xeroOrgId: string;
  xeroOrgName: string | null;
  employeeCount: number | null;
  needsReviewCount: number | null;
  payrollEnabled: boolean;
  reconnectRequired: boolean;
}

export interface XeroEmployee {
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: 'ACTIVE' | 'TERMINATED' | 'LEAVE';
  startDate: string | null;
  terminationDate: string | null;
  jobTitle: string | null;
  classification: string | null;
  ordinaryEarningsRateName: string | null;
  payBasis: 'HOURLY' | 'SALARY' | 'COMMISSION' | null;
  hoursPerWeek: number | null;
  employmentBasis: 'FULLTIME' | 'PARTTIME' | 'CASUAL' | 'LABOURHIRE' | null;
  isApprentice: boolean | null;
  dateOfBirth: string | null;
  ageYears: number | null;
  currentPayRate: number | null;
  incomeType: string | null;
  employmentType: string | null;
  isAuthorisedToApproveLeave: boolean | null;
}

export interface SuggestedAward {
  code: string | null;
  name: string | null;
  sourcePdfUrl: string | null;
  documentId: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
}

export interface EmployeeReviewRow {
  employee: XeroEmployee;
  missingFields: string[];
  suggestedAward: SuggestedAward;
  savedGrade: string | null;
}

export interface PayrollReview {
  clientId: string;
  clientName: string;
  industry: string | null;
  xeroOrgName: string | null;
  communicationLanguage?: 'en' | 'fa';
  asOf: string;
  rows: EmployeeReviewRow[];
  summary: {
    total: number;
    active: number;
    onLeave: number;
    needsReview: number;
    awardMatched: number;
  };
  error?: string;
  missingScope?: string;
}

export interface RateRow {
  classification: string;
  baseHourlyRate: number | null;
  weeklyRate: number | null;
  penalties: Record<string, string>;
}

export interface MinWageLookup {
  hourlyRate: number | null;
  weeklyRate: number | null;
  basis: 'adult' | 'junior' | 'apprentice' | 'unknown';

  awardCode: string | null;
  awardName: string | null;
  sourcePdfUrl: string | null;
  documentId: string | null;

  employeeCategory: 'adult' | 'junior' | 'apprentice';
  employmentType: 'permanent' | 'casual';
  juniorAge: string | null;

  matchedRate: RateRow | null;
  allRates: RateRow[];

  calculation?: {
    permanentBaseRate: number | null;
    casualLoadingPct: number | null;
    standardHoursPerWeek: number | null;
    weeklyFromHourly: number | null;
    annualEstimate: number | null;
  };

  currentPayRate: number | null;
  compliance: 'compliant' | 'below' | 'unknown';
  gradeConfirmed: boolean;

  rationale: string;
  confidence: 'high' | 'medium' | 'low';
  missingInputs: string[];
  employeeInputs?: {
    classification: string | null;
    jobTitle: string | null;
    ageYears: number | null;
    employmentBasis: string | null;
    payBasis: string | null;
    hoursPerWeek: number | null;
    isApprentice: boolean | null;
    currentPayRate: number | null;
  };
}

export async function listPayrollClients(): Promise<{ clients: PayrollClientSummary[] }> {
  const r = await apiClient.get<{ clients: PayrollClientSummary[] }>('/payroll/clients');
  return r.data;
}

export async function getPayrollReview(clientId: string): Promise<{ review: PayrollReview }> {
  const r = await apiClient.get<{ review: PayrollReview }>(`/payroll/clients/${encodeURIComponent(clientId)}`);
  return r.data;
}

export async function lookupMinWage(clientId: string, employeeId: string): Promise<{ lookup: MinWageLookup }> {
  const r = await apiClient.post<{ lookup: MinWageLookup }>(
    `/payroll/clients/${encodeURIComponent(clientId)}/employees/${encodeURIComponent(employeeId)}/min-wage`,
    {},
  );
  return r.data;
}

export async function requestMissingInfo(
  clientId: string, employeeId: string, message?: string,
): Promise<{ message: { id: string; body: string; senderName: string | null; createdAt: string } }> {
  const r = await apiClient.post(
    `/payroll/clients/${encodeURIComponent(clientId)}/employees/${encodeURIComponent(employeeId)}/request-info`,
    message ? { message } : {},
  );
  return r.data;
}

export interface EmployeeRawResponse {
  xeroOrgName: string | null;
  asOf: string;
  normalized: XeroEmployee;
  raw: Record<string, unknown>;
  error?: string;
  message?: string;
  missingScope?: string;
}

export async function getEmployeeRaw(clientId: string, employeeId: string): Promise<EmployeeRawResponse> {
  const r = await apiClient.get<EmployeeRawResponse>(
    `/payroll/clients/${encodeURIComponent(clientId)}/employees/${encodeURIComponent(employeeId)}/raw`,
  );
  return r.data;
}

/** Set the client's communication language (firm→client messages use it). */
export async function setClientLanguage(
  clientId: string, language: 'en' | 'fa',
): Promise<{ saved: boolean; language: string }> {
  const r = await apiClient.post(
    `/payroll/clients/${encodeURIComponent(clientId)}/language`,
    { language },
  );
  return r.data;
}

/** Persist (or clear, when grade is null) the firm-confirmed grade for an employee. */
export async function setEmployeeGrade(
  clientId: string, employeeId: string, grade: string | null, awardCode?: string | null,
): Promise<{ saved?: boolean; cleared?: boolean; grade?: string }> {
  const r = await apiClient.post(
    `/payroll/clients/${encodeURIComponent(clientId)}/employees/${encodeURIComponent(employeeId)}/grade`,
    { grade, awardCode },
  );
  return r.data;
}
